import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  ArrowRight,
  CheckCircle2,
  Gift,
  HardDrive,
  Heart,
  RefreshCw,
  Send,
  Sparkles,
  TicketCheck,
  UserCheck,
  Users,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/sonner';
import { useMembership } from '@/contexts/MembershipContext';
import {
  projectMembershipSeatAuthority,
  type MembershipPlanCapacity,
  type MembershipSeatAuthorityReport,
} from '@/governor/membership/MembershipSeatAuthority';
import {
  projectMembershipDashboardCounters,
} from '@/governor/membership/MembershipDashboardCounterAuthority';
import { supabase } from '@/lib/supabase';
import KinsSpacePlansModal from '@/components/kinsspace/KinsSpacePlansModal';

interface MembershipOperationsCenterProps {
  userId: string;
  profileClaimed: boolean;
  onInviteFamily: () => void;
  onSponsorFamily: () => void;
}

interface InviteRuntimeRow {
  id: number | string;
  invite_type?: 'FAMILY_PROFILE' | 'SPONSORED_SEAT';
  status: string | null;
  accepted_user_id: string | null;
  seat_reservation_expires_at?: string | null;
  seat_returned_at?: string | null;
  expires_at?: string | null;
}

interface InviteSummary {
  familySent: number;
  familyPending: number;
  sponsoredPending: number;
  accepted: number;
  expired: number;
  returned: number;
}

const EMPTY_SUMMARY: InviteSummary = {
  familySent: 0,
  familyPending: 0,
  sponsoredPending: 0,
  accepted: 0,
  expired: 0,
  returned: 0,
};

function resolveCapacity(
  plan: string,
): MembershipPlanCapacity {
  switch (plan) {
    case 'monthly_premium':
      return 5;
    case 'annual_founding':
      return 10;
    case 'annual_standard':
      return 30;
    default:
      return 1;
  }
}

function resolvePlanLabel(
  plan: string,
  isPaid: boolean,
): string {
  if (!isPaid) return 'Family Contributor';

  switch (plan) {
    case 'annual_founding':
      return "Founder Membership";
    case 'annual_standard':
      return 'Annual Membership';
    case 'monthly_premium':
      return 'Monthly Membership';
    default:
      return 'KinsCircle Membership';
  }
}

function isLifecycleSchemaError(
  message: string,
): boolean {
  const value = message.toLowerCase();

  return (
    value.includes('schema cache') ||
    value.includes('invite_type') ||
    value.includes('seat_reservation_expires_at') ||
    value.includes('seat_returned_at')
  );
}

const MembershipOperationsCenter: React.FC<
  MembershipOperationsCenterProps
> = ({
  userId,
  profileClaimed,
  onInviteFamily,
  onSponsorFamily,
}) => {
  const {
    isPaid,
    isStaff,
    hasPaidCapability,
    plan,
    openMembershipModal,
  } = useMembership();

  const [loading, setLoading] = useState(true);
  const [kinsSpaceOpen, setKinsSpaceOpen] =
    useState(false);
  const [schemaAvailable, setSchemaAvailable] =
    useState(false);
  const [summary, setSummary] =
    useState<InviteSummary>(EMPTY_SUMMARY);
  const [claimedFamilyProfiles, setClaimedFamilyProfiles] =
    useState(0);
  const [unclaimedFamilyProfiles, setUnclaimedFamilyProfiles] =
    useState(0);
  const [seatReport, setSeatReport] =
    useState<MembershipSeatAuthorityReport>(() =>
      projectMembershipSeatAuthority({
        planCapacity: resolveCapacity(plan),
      }),
    );

  const planLabel = resolvePlanLabel(
    plan,
    isPaid || isStaff,
  );

  const hasPaidAccess =
    hasPaidCapability || isStaff;

  // A sponsored seat grants family capabilities but does
  // not expose another subscriber's sponsorship inventory.
  const ownsSponsorshipAuthority =
    isPaid || isStaff;

  const isSponsoredOnlyMember =
    hasPaidCapability &&
    !ownsSponsorshipAuthority;

  const loadRuntime = useCallback(async () => {
    if (!userId) return;

    setLoading(true);

    try {
      const {
        data,
        error,
      } = await supabase.rpc(
        'get_membership_dashboard_counters',
      );

      if (error) {
        throw error;
      }

      const authority =
        projectMembershipDashboardCounters(
          data && typeof data === 'object'
            ? data
            : {},
        );

      if (authority.status !== 'PASS') {
        throw new Error(
          'Membership counter authority returned an invalid state.',
        );
      }

      setSchemaAvailable(true);
      setSeatReport(authority.seatReport);

      setSummary({
        familySent:
          authority.invitations.familyInvitesSent,
        familyPending:
          authority.invitations.familyInvitesPending,
        sponsoredPending:
          authority.seatReport
            .reservedSponsoredSeatCount,
        accepted:
          authority.invitations.acceptedInvites,
        expired:
          authority.invitations.expiredInvites,
        returned:
          authority.invitations
            .returnedSponsoredSeats,
      });

      setClaimedFamilyProfiles(
        authority.familyProfiles
          .claimedFamilyProfiles,
      );

      setUnclaimedFamilyProfiles(
        authority.familyProfiles
          .unclaimedFamilyProfiles,
      );
    } catch (error) {
      console.error(
        '[MembershipOperationsCenter] authoritative counter load failed',
        error,
      );

      toast.error(
        'Membership counters could not be refreshed.',
      );

      setSchemaAvailable(false);
      setSummary(EMPTY_SUMMARY);
      setClaimedFamilyProfiles(0);
      setUnclaimedFamilyProfiles(0);

      setSeatReport(
        projectMembershipSeatAuthority({
          planCapacity: resolveCapacity(plan),
        }),
      );
    } finally {
      setLoading(false);
    }
  }, [plan, userId]);

  useEffect(() => {
    void loadRuntime();

    const onFocus = () => {
      void loadRuntime();
    };

    window.addEventListener('focus', onFocus);

    return () => {
      window.removeEventListener(
        'focus',
        onFocus,
      );
    };
  }, [loadRuntime]);

  const welcomeMessage = useMemo(() => {
    if (
      seatReport.activeSponsoredSeatCount > 0
    ) {
      return `You've welcomed ${seatReport.activeSponsoredSeatCount} sponsored family member${
        seatReport.activeSponsoredSeatCount === 1
          ? ''
          : 's'
      } home.`;
    }

    if (summary.accepted > 0) {
      return `Your family has welcomed ${summary.accepted} invitee${
        summary.accepted === 1 ? '' : 's'
      }.`;
    }

    return 'Your next family connection is one invitation away.';
  }, [
    seatReport.activeSponsoredSeatCount,
    summary.accepted,
  ]);

  const openUpgrade = () => {
    openMembershipModal();
  };

  const openKinsSpace = () => {
    setKinsSpaceOpen(true);
  };

  return (
    <section
      aria-label="Membership Operations Center"
      className="mt-5 space-y-4"
    >
      <div className="rounded-3xl border border-violet-200 bg-gradient-to-br from-white via-violet-50 to-amber-50 p-5 shadow-sm">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-violet-700">
              <Heart className="h-4 w-4" />
              Membership & Invitations
            </div>

            <h3 className="mt-1 font-serif text-2xl font-bold text-slate-950">
              Everything your family needs,
              right here
            </h3>

            <p className="mt-1 text-sm text-slate-600">
              {welcomeMessage}
            </p>
          </div>

          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void loadRuntime()}
            disabled={loading}
            className="rounded-full border-violet-300 text-violet-800"
          >
            <RefreshCw
              className={`mr-2 h-4 w-4 ${
                loading ? 'animate-spin' : ''
              }`}
            />
            Refresh
          </Button>
        </div>

        <div className="mt-5 grid gap-4 xl:grid-cols-2">
          <article className="rounded-2xl border border-violet-200 bg-white p-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-violet-700">
                  My Membership
                </div>
                <div className="mt-1 font-serif text-xl font-bold text-slate-950">
                  {planLabel}
                </div>
              </div>

              <span className="rounded-full bg-violet-100 px-3 py-1 text-xs font-bold text-violet-800">
                {profileClaimed
                  ? 'Profile claimed'
                  : 'Finish profile'}
              </span>
            </div>

            <p className="mt-3 text-sm text-slate-600">
              {isSponsoredOnlyMember
                ? 'Your sponsored family access is active. You can use shared family features, share public and family invitations, add KinsSpace, or purchase your own membership.'
                : hasPaidAccess
                  ? 'Your paid membership keeps your family connected with messaging, calls, media, and family experiences.'
                  : 'You can build the tree, participate on the Message Board, manage your profile, and share up to five photos.'}
            </p>

            <div className="mt-4 grid grid-cols-2 gap-2">
              <Button
                type="button"
                onClick={onInviteFamily}
                className="bg-violet-700 text-white hover:bg-violet-800"
              >
                <Send className="mr-2 h-4 w-4" />
                Invite Family
              </Button>

              <Button
                type="button"
                variant="outline"
                onClick={openUpgrade}
                className="border-amber-300 text-amber-900"
              >
                <Sparkles className="mr-2 h-4 w-4" />
                {ownsSponsorshipAuthority
                  ? 'View Membership'
                  : isSponsoredOnlyMember
                    ? 'Buy My Own Membership'
                    : 'Upgrade'}
              </Button>
            </div>
          </article>

          {ownsSponsorshipAuthority && (
            <article className="rounded-2xl border border-emerald-200 bg-white p-4">
              <div className="flex items-center gap-2">
              <Gift className="h-5 w-5 text-emerald-700" />
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-emerald-700">
                  Sponsored Memberships
                </div>
                <div className="font-serif text-lg font-bold text-slate-950">
                  Help family feel at home
                </div>
              </div>
            </div>

            <div className="mt-4 grid grid-cols-5 gap-2 text-center">
              <Metric
                value={seatReport.planCapacity}
                label="Total"
              />
              <Metric
                value={seatReport.occupiedSeatCount}
                label="Used"
              />
              <Metric
                value={
                  seatReport.reservedSponsoredSeatCount
                }
                label="Reserved"
              />
              <Metric
                value={seatReport.availableSeatCount}
                label="Ready"
              />
              <Metric
                value={seatReport.returnedSeatCount}
                label="Returned"
              />
            </div>

            <Button
              type="button"
              variant="outline"
              onClick={onSponsorFamily}
              disabled={
                !seatReport.canReserveSponsoredSeat &&
                seatReport.planCapacity > 1
              }
              className="mt-4 w-full border-emerald-300 text-emerald-900"
            >
              <UserCheck className="mr-2 h-4 w-4" />
              {seatReport.canReserveSponsoredSeat
                ? 'Sponsor a Family Member'
                : seatReport.planCapacity === 1
                  ? 'Choose a Sponsorship Plan'
                  : 'All Sponsored Seats Are Helping Family'}
            </Button>
            </article>
          )}

          <article className="rounded-2xl border border-pink-200 bg-white p-4">
            <div className="flex items-center gap-2">
              <TicketCheck className="h-5 w-5 text-pink-700" />
              <div className="text-xs font-bold uppercase tracking-wider text-pink-700">
                Invitation Center
              </div>
            </div>

            <div className="mt-4 grid grid-cols-3 gap-2 text-center sm:grid-cols-4 xl:grid-cols-8">
              <Metric
                value={summary.familySent}
                label="Family"
              />
              {ownsSponsorshipAuthority && (
                <Metric
                  value={summary.sponsoredPending}
                  label="Sponsored"
                />
              )}
              <Metric
                value={summary.familyPending}
                label="Pending"
              />
              <Metric
                value={summary.accepted}
                label="Joined"
              />
              <Metric
                value={summary.expired}
                label="Expired"
              />
              {ownsSponsorshipAuthority && (
                <Metric
                  value={summary.returned}
                  label="Returned"
                />
              )}
              <Metric
                value={claimedFamilyProfiles}
                label="Claimed"
              />
              <Metric
                value={unclaimedFamilyProfiles}
                label="Unclaimed"
              />
            </div>

            <p className="mt-3 text-xs text-slate-500">
              {ownsSponsorshipAuthority
                ? 'Public and family invitations remain available. Sponsored invitations reserve capacity for seven days.'
                : 'You may share public and family invitations. Sponsored-seat invitations become available after you purchase your own qualifying membership.'}
            </p>

            <Button
              type="button"
              variant="outline"
              onClick={onInviteFamily}
              className="mt-4 w-full border-pink-300 text-pink-800"
            >
              <Users className="mr-2 h-4 w-4" />
              Open Invitation Center
            </Button>
          </article>

          <article className="rounded-2xl border border-amber-200 bg-white p-4">
            <div className="flex items-center gap-2">
              <HardDrive className="h-5 w-5 text-amber-700" />
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-amber-700">
                  KinsSpace
                </div>
                <div className="font-serif text-lg font-bold text-slate-950">
                  More room for family memories
                </div>
              </div>
            </div>

            <p className="mt-3 text-sm text-slate-600">
              Keep photos, videos, and family history
              together. Included and purchased storage
              is shared only with members of your
              governed family circle.
            </p>

            <Button
              type="button"
              variant="outline"
              onClick={openKinsSpace}
              className="mt-4 w-full border-amber-300 text-amber-900"
            >
              Explore KinsSpace
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </article>
        </div>

        {!schemaAvailable && (
          <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900">
            <div className="flex items-start gap-2">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
              <p>
                Family invitations are available.
                Sponsored-seat controls will become
                fully active after the approved
                membership database migration runs.
              </p>
            </div>
          </div>
        )}
      </div>

      <KinsSpacePlansModal
        open={kinsSpaceOpen}
        onOpenChange={setKinsSpaceOpen}
        hasPaidMembership={hasPaidAccess}
        onMembershipRequired={
          openMembershipModal
        }
      />
    </section>
  );
};

const Metric: React.FC<{
  value: number;
  label: string;
}> = ({ value, label }) => (
  <div className="rounded-xl bg-slate-50 px-2 py-2">
    <div className="text-lg font-extrabold tabular-nums text-slate-950">
      {value}
    </div>
    <div className="text-[9px] font-bold uppercase tracking-wide text-slate-500">
      {label}
    </div>
  </div>
);

export default MembershipOperationsCenter;
